import { motion, useMotionValue, useSpring, useTransform, useReducedMotion } from "framer-motion";
import { ArrowDown, Sparkles } from "lucide-react";
import DashboardMockup from "./DashboardMockup";
import { scrollToSection } from "./scrollTo";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero() {
  const reduceMotion = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 70, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 70, damping: 18 });

  const handleMouseMove = (e) => {
    if (reduceMotion) return;
    const rect = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - rect.left) / rect.width - 0.5);
    my.set((e.clientY - rect.top) / rect.height - 0.5);
  };

  return (
    <section
      id="product"
      className="relative flex min-h-screen items-center overflow-hidden pt-32 pb-20"
      onMouseMove={handleMouseMove}
      data-testid="hero-section"
    >
      <div className="hero-grid absolute inset-0" aria-hidden="true" />
      <div
        className="absolute -top-40 right-[-10%] h-[560px] w-[560px] rounded-full bg-[#00FF66]/15 blur-[140px]"
        aria-hidden="true"
      />
      <div
        className="absolute bottom-[-20%] left-[-10%] h-[420px] w-[420px] rounded-full bg-[#A8E6CF]/10 blur-[120px]"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
            className="mb-8 inline-flex items-center gap-2 rounded-full border border-[#00FF66]/30 bg-[#00FF66]/5 px-4 py-1.5"
            data-testid="hero-release-badge"
          >
            <Sparkles className="h-3.5 w-3.5 text-[#00FF66]" />
            <span className="text-xs font-semibold uppercase tracking-[0.22em] text-[#00FF66]">
              Now live · 7-day free trial
            </span>
          </motion.div>

          <h1 className="font-display text-5xl font-black leading-[0.95] tracking-tighter md:text-6xl xl:text-7xl">
            <MaskedLine delay={0.45}>Project tracking</MaskedLine>
            <MaskedLine delay={0.58}>that actually</MaskedLine>
            <MaskedLine delay={0.71}>
              <span className="text-[#00FF66]">ships.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.95, ease: EASE }}
            className="mt-8 max-w-md text-base leading-relaxed text-white/55 md:text-lg"
          >
            Kanban, workload and time-off in one signal. Vektor gives teams tired
            of noise a single place to see who&apos;s doing what — and what&apos;s next.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <motion.a
              href="/signup"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.96 }}
              className="rounded-full bg-[#00FF66] px-8 py-4 text-sm font-bold text-black shadow-[0_0_44px_rgba(0,255,102,0.4)]"
              data-testid="hero-start-trial-button"
            >
              Start free trial
            </motion.a>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.96 }}
              onClick={() => scrollToSection("#manifesto")}
              className="flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-8 py-4 text-sm font-semibold text-white backdrop-blur-md transition-colors duration-200 hover:border-[#00FF66]/50 hover:text-[#00FF66]"
              data-testid="hero-see-product-button"
            >
              Explore the product
              <ArrowDown className="h-4 w-4" />
            </motion.button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.25 }}
            className="mt-4 text-xs font-medium text-white/35"
            data-testid="hero-cta-microcopy"
          >
            7-day free trial · no credit card required
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.35 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-2 text-xs font-medium uppercase tracking-[0.18em] text-white/35"
            data-testid="hero-stats-row"
          >
            <span>6 status lanes</span>
            <span>Live analytics</span>
            <span>Zero spreadsheets</span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 60 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, delay: 0.9, ease: EASE }}
          className="lg:col-span-6"
          style={{ perspective: 1200 }}
        >
          <motion.div
            style={reduceMotion ? {} : { rotateX, rotateY, transformStyle: "preserve-3d" }}
          >
            <DashboardMockup />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
